import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';
import { captureSignupStarted } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Facebook, Instagram, LinkedIn-Android and similar in-app browsers are webviews.
 *  Google refuses OAuth inside them, so we warn before the user hits a dead end. */
export const isInAppBrowser = () => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  return /FBAN|FBAV|FB_IAB|Instagram|LinkedInApp|Twitter|Snapchat|Pinterest|Line\//i.test(ua);
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GoogleSignInButton = ({ label = 'Continue with Google' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inApp = isInAppBrowser();

  const handleClick = async () => {
    setError('');
    setLoading(true);
    captureSignupStarted('google');
    try {
      // The consent screen is our own Google project, so it says VerifAEye
      const res = await axios.get(`${API_URL}/api/auth/google/login`);
      window.location.href = res.data.authorization_url;
    } catch (err) {
      setLoading(false);
      setError('Google sign-in is unavailable right now — please use your email address.');
    }
  };

  if (inApp) {
    return (
      <div
        className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900"
        data-testid="google-inapp-warning"
      >
        Google sign-in doesn't work inside the {'LinkedIn/Facebook'} in-app browser. Use the email form
        above, or open verifaeye.com.au in Safari or Chrome to continue with Google.
      </div>
    );
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={handleClick}
        disabled={loading}
        className="h-12 w-full border-slate-300 text-slate-700 hover:bg-slate-50"
        data-testid="google-signin-btn"
      >
        {loading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1a11 11 0 0 0-9.82 6.05l3.66 2.84c.87-2.6 3.3-4.51 6.16-4.51z" />
          </svg>
        )}
        {label}
      </Button>
      <p className="mt-2 text-center text-xs text-slate-500">
        Google only shares your name and email with VerifAEye.
      </p>
      {error && (
        <p className="mt-2 text-center text-xs text-red-600" data-testid="google-signin-error">
          {error}
        </p>
      )}
    </>
  );
};

export default GoogleSignInButton;
